import React from 'react'
import {Button, HStack, Input, useNumberInput, useStyleConfig} from '@chakra-ui/react'
import {useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {MinusIcon, AddIcon} from '../../components/icons'
import useUtils from '../../custom-hooks/use-utils-helper'
import {remCalc} from '../../theme/_utils'
import {useLocation} from 'react-router-dom'

const QuantityPicker = ({
    productName,
    hideIncDecButtons = false,
    variant,
    isReturnOrder = false,
    ...props
}) => {
    const intl = useIntl()
    const style = useStyleConfig('QuantityPicker', {variant})
    const location = useLocation()
    const currentPage = useUtils().currentPage(location.pathname)
    const {isAccount} = currentPage

    const {getInputProps, getIncrementButtonProps, getDecrementButtonProps} = useNumberInput({
        ...props,
        focusInputOnChange: false,
        onFocus: (e) => {
            // eslint-disable-next-line react/prop-types
            const {onFocus} = props
            e.target.select()
            // eslint-disable-next-line react/prop-types
            onFocus?.call(this, e)
        },
    })

    const inc = getIncrementButtonProps({
        'aria-label': intl.formatMessage(
            {
                defaultMessage: 'Increment Quantity for {productName}',
                id: 'product_view.label.assistive_msg.quantity_increment',
            },
            {productName},
        ),
    })

    const dec = getDecrementButtonProps({
        'aria-label': intl.formatMessage(
            {
                defaultMessage: 'Decrement Quantity for {productName}',
                id: 'product_view.label.assistive_msg.quantity_decrement',
            },
            {productName},
        ),
    })

    const input = getInputProps({
        'aria-label': intl.formatMessage({
            defaultMessage: 'Quantity',
            id: 'product_view.label.quantity',
        }),
    })

    // Accessibility improvements:
    inc.tabIndex = 0
    dec.tabIndex = 0

    inc.onKeyDown = (evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
            evt.preventDefault()
            evt.key = 'ArrowUp'
            input.onKeyDown(evt)
        }
    }

    dec.onKeyDown = (evt) => {
        if (evt.key === ' ' || evt.key === 'Enter') {
            evt.preventDefault()
            evt.key = 'ArrowDown'
            input.onKeyDown(evt)
        }
    }

    return (
        <HStack {...style?.stack}>
            {!hideIncDecButtons && (
                <Button {...dec} tabIndex={0} data-testid="dec-button" display={'flex'}>
                    <MinusIcon
                        {...(props?.value > (props?.min ?? 1)
                            ? {...style.decButtonActive}
                            : {...style?.decButton})}
                        data-testid="quantity-decrement"
                        {...style.quantityIcon}
                    />
                </Button>
            )}
            <Input
                {...(isReturnOrder ? style?.inputReturn : style?.input)} // Apply conditional style
                {...input}
                data-testid="spinbutton"
                borderRadius={isAccount ? remCalc(2) : 'unset'}
            />
            {!hideIncDecButtons && (
                <Button {...inc} tabIndex={0} data-testid="inc-button" display={'flex'}>
                    <AddIcon
                        {...(props.value >= props?.max
                            ? {...style?.incButton}
                            : {...style.incButtonActive})}
                        data-testid="quantity-increment"
                        {...style.quantityIcon}
                    />
                </Button>
            )}
        </HStack>
    )
}

QuantityPicker.propTypes = {
    productName: PropTypes.string,
    hideIncDecButtons: PropTypes.bool,
    variant: PropTypes.string,
    value: PropTypes.any,
    min: PropTypes.number,
    max: PropTypes.number,
    isReturnOrder: PropTypes.bool,
}

export default QuantityPicker
